import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useRemoteSession } from '@/hooks/useRemoteSession';
import { Terminal as TerminalIcon, Loader2, AlertCircle, XCircle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TerminalProps {
  deviceId: string;
}

interface TerminalOutput {
  id: string;
  command: string;
  output: string;
  status: 'pending' | 'executing' | 'completed' | 'failed' | 'timeout';
  timestamp: Date;
}

export const Terminal = ({ deviceId }: TerminalProps) => {
  const [command, setCommand] = useState('');
  const [terminalOutput, setTerminalOutput] = useState<TerminalOutput[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [sessionError, setSessionError] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const { activeSession, isLoadingSession, startSession, sendCommand } = useRemoteSession(deviceId);

  // Auto-scroll to bottom when new output is added
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [terminalOutput]);

  // Subscribe to terminal command updates
  useEffect(() => {
    if (!activeSession?.id) return;

    const channel = supabase
      .channel('terminal-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'terminal_commands',
          filter: `session_id=eq.${activeSession.id}`
        },
        (payload) => {
          console.log('Terminal command update:', payload);
          
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const cmd = payload.new;
            setTerminalOutput(prev => {
              const existing = prev.find(o => o.id === cmd.id);
              const newOutput: TerminalOutput = {
                id: cmd.id,
                command: cmd.command,
                output: cmd.output || '',
                status: cmd.status,
                timestamp: new Date(cmd.created_at)
              };
              
              if (existing) {
                return prev.map(o => o.id === cmd.id ? newOutput : o);
              }
              return [...prev, newOutput];
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeSession?.id]);

  const handleStartSession = async () => {
    console.log('Starting terminal session');
    setIsConnecting(true);
    setSessionError(false);
    setTerminalOutput([]);
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    try {
      await startSession.mutateAsync('terminal');
      console.log('Terminal session started successfully');
      
      // Set a timeout to show error if session doesn't appear
      timeoutRef.current = setTimeout(() => {
        if (!activeSession) {
          console.warn('Session created but not appearing in UI after 10 seconds');
          setIsConnecting(false);
          setSessionError(true);
          toast.error('Terminal session timeout. Please try again.');
        }
      }, 10000);
    } catch (error) {
      console.error('Failed to start session:', error);
      setIsConnecting(false);
      setSessionError(true);
    }
  };

  const handleStopSession = async () => {
    console.log('Stopping terminal session');
    if (activeSession?.id) {
      await supabase
        .from('remote_sessions')
        .update({ 
          status: 'ended',
          ended_at: new Date().toISOString()
        })
        .eq('id', activeSession.id);
    }
    setTerminalOutput([]);
  };

  const handleSendCommand = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!command.trim() || !activeSession?.id) {
      console.log('Cannot send command: missing command or session');
      return;
    }

    console.log('Sending command:', command);
    const currentCommand = command;
    setCommand('');
    
    // Send command via edge function - it will create a pending command in the database
    sendCommand.mutate(currentCommand, {
      onError: (error) => {
        console.error('Error sending command:', error);
        setTerminalOutput(prev => [...prev, {
          id: Date.now().toString(),
          command: currentCommand,
          output: `Error: ${error.message}`,
          status: 'failed',
          timestamp: new Date()
        }]);
      }
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-3 w-3 text-green-400" />;
      case 'failed':
      case 'timeout':
        return <XCircle className="h-3 w-3 text-red-400" />;
      case 'pending':
      case 'executing':
        return <Loader2 className="h-3 w-3 animate-spin text-yellow-400" />;
      default:
        return null;
    }
  };

  if (isLoadingSession) {
    return (
      <Card className="p-6 hover:shadow-lg hover:scale-100">
        <div className="flex items-center justify-center space-x-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading terminal...</span>
        </div>
      </Card>
    );
  }

  // Once session is active, turn off connecting state and clear timeout
  useEffect(() => {
    if (activeSession?.status === 'active') {
      console.log('Active session detected, clearing connecting state');
      setIsConnecting(false);
      setSessionError(false);
      
      // Clear the timeout since session is now active
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [activeSession]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!activeSession || activeSession.status !== 'active') {
    return (
      <Card className="p-6 hover:shadow-lg hover:scale-100">
        <div className="space-y-4">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <TerminalIcon className="h-5 w-5" />
            <span>Remote Terminal</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Start a terminal session to execute commands on this device remotely.
          </p>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Note: A device agent must be running on the device for commands to execute.
            </AlertDescription>
          </Alert>
          {sessionError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Failed to start terminal session. Please try again.
              </AlertDescription>
            </Alert>
          )}
          <Button 
            onClick={handleStartSession}
            disabled={isConnecting || startSession.isPending}
            className="w-full"
          >
            {(isConnecting || startSession.isPending) ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <TerminalIcon className="mr-2 h-4 w-4" />
                {sessionError ? 'Retry' : 'Start Terminal Session'}
              </>
            )}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 hover:shadow-lg hover:scale-100">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <TerminalIcon className="h-5 w-5" />
            <span className="font-medium">Remote Terminal</span>
            <span className="flex items-center text-sm text-green-600">
              <span className="mr-1 h-2 w-2 rounded-full bg-green-600" />
              Session Active
            </span>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleStopSession}
          >
            <XCircle className="mr-2 h-4 w-4" />
            Stop Session
          </Button>
        </div>

        <ScrollArea className="h-96 rounded-md border bg-black p-4 font-mono text-sm">
          <div ref={scrollRef} className="space-y-3">
            {terminalOutput.length === 0 ? (
              <div className="text-muted-foreground">
                Terminal ready. Type a command and press Enter.
              </div>
            ) : (
              terminalOutput.map((item) => (
                <div key={item.id} className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400">$</span>
                    <span className="text-green-400">{item.command}</span>
                    {getStatusIcon(item.status)}
                  </div>
                  {item.status === 'pending' || item.status === 'executing' ? (
                    <div className="text-yellow-400 text-xs ml-4">
                      {item.status === 'pending' ? 'Waiting for device agent...' : 'Executing...'}
                    </div>
                  ) : item.output ? (
                    <div className={`ml-4 whitespace-pre-wrap ${
                      item.status === 'failed' || item.status === 'timeout' ? 'text-red-400' : 'text-gray-300'
                    }`}>
                      {item.output}
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        <form onSubmit={handleSendCommand} className="flex space-x-2">
          <Input
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            placeholder="Enter command..."
            className="font-mono"
            disabled={sendCommand.isPending}
          />
          <Button 
            type="submit" 
            disabled={!command.trim() || sendCommand.isPending}
          >
            {sendCommand.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Execute'
            )}
          </Button>
        </form>
      </div>
    </Card>
  );
};
